/**
 * MonthGrid Component
 *
 * Renders a 7-column calendar grid for a given month, with day name headers
 * (Lunes through Domingo) and DayCell components for each day. Uses the
 * getMonthGrid utility to compute the grid layout and getBookingsForDay to
 * assign bookings to each cell.
 *
 * Validates: Requirements 2.1, 2.3
 * - Requirement 2.1: Day numbers arranged in a 7-column layout (Lunes through Domingo).
 * - Requirement 2.3: Booking indicators displayed within the corresponding day cells.
 */

import { tokens } from '../../../design-system/tokens';
import { getMonthGrid, getBookingsForDay, isSameCalendarDay, preferInternalBookings } from '../calendarView.utils';
import type { CalendarViewBooking } from '../calendarView.types';
import { DayCell } from './DayCell';

// ============================================
// TYPES
// ============================================

export interface MonthGridProps {
  /** Year to display */
  year: number;
  /** Month to display (1-12) */
  month: number;
  /** All bookings for the displayed period */
  bookings: CalendarViewBooking[];
  /** Today's date, used to highlight the current day */
  today: Date;
  /** Día seleccionado para resaltar y filtrar lista (mismo mes que `year`/`month` o null) */
  selectedDate: Date | null;
  /** Al hacer clic en un día del mes actual */
  onSelectDay?: (date: Date) => void;
}

// ============================================
// CONSTANTS
// ============================================

/** Day name headers in Spanish (Monday-first week) */
const DAY_HEADERS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

// ============================================
// MAIN COMPONENT
// ============================================

/**
 * MonthGrid — Renders the full month calendar grid.
 *
 * Computes the grid of weeks using getMonthGrid, then for each day determines
 * which bookings fall on that day and whether it is today. Renders a header row
 * with abbreviated Spanish day names and a DayCell for each day.
 *
 * @example
 * ```tsx
 * <MonthGrid
 *   year={2025}
 *   month={6}
 *   bookings={bookings}
 *   today={new Date()}
 *   selectedDate={null}
 *   onSelectDay={(d) => setSelected(d)}
 * />
 * ```
 */
export function MonthGrid({
  year,
  month,
  bookings,
  today,
  selectedDate,
  onSelectDay,
}: MonthGridProps) {
  const weeks = getMonthGrid(year, month);

  return (
    <div>
      {/* Day name headers */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: tokens.spacing[1],
          marginBottom: tokens.spacing[2],
        }}
      >
        {DAY_HEADERS.map((name) => (
          <div
            key={name}
            style={{
              textAlign: 'center',
              fontSize: tokens.typography.fontSize.xs,
              fontWeight: tokens.typography.fontWeight.semibold,
              color: tokens.colors.neutral[500],
              padding: tokens.spacing[1],
              textTransform: 'uppercase',
            }}
          >
            {name}
          </div>
        ))}
      </div>

      {/* Day cells grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: tokens.spacing[1],
        }}
      >
        {weeks.map((week, weekIndex) =>
          week.map((dayInfo) => {
            const dayBookings = preferInternalBookings(getBookingsForDay(bookings, dayInfo.date));
            const isToday = isSameCalendarDay(dayInfo.date, today);
            const isSelected =
              Boolean(selectedDate) &&
              dayInfo.isCurrentMonth &&
              isSameCalendarDay(dayInfo.date, selectedDate!);

            return (
              <DayCell
                key={`${weekIndex}-${dayInfo.day}-${dayInfo.isCurrentMonth}`}
                day={dayInfo.day}
                isCurrentMonth={dayInfo.isCurrentMonth}
                isToday={isToday}
                isSelected={isSelected}
                bookings={dayBookings}
                onSelect={
                  dayInfo.isCurrentMonth && onSelectDay ?
                    () => onSelectDay(dayInfo.date)
                  : undefined
                }
              />
            );
          })
        )}
      </div>
    </div>
  );
}

export default MonthGrid;
